// HM-324 cluster 15.5: Drawer dettaglio camera slide-in da destra 380px.
// SPEC plan operativo §15.5.6:
//   - Width 380px, slide-in da destra (transform translateX 100% -> 0)
//   - Backdrop scuro rgba(45,36,29,0.5)
//   - Animation 300ms ease-out
//   - Chiusura: bottone X header / click backdrop / ESC keypress
//   - Contenuti: header + 5 panel-row dettagli + 4 azioni rapide
//
// Riusa la logica RoomDetailPanel cluster 15.3 (che era statico) come
// contenuto, ma incapsulata in un drawer overlay.

import { Button } from '@hotel-manager/ui/components';
import { Tooltip, TooltipContent, TooltipTrigger } from '@hotel-manager/ui/components';
import {
  BedDouble,
  CircleCheck,
  HM_ICON_STROKE_WIDTH,
  KeyRound,
  type LucideIcon,
  MessageCircle,
  TriangleAlert,
  Wind,
  Wrench,
  X,
} from '@hotel-manager/ui/icons';
import { cn } from '@hotel-manager/ui/lib/cn';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  adminSetRoomFree,
  fetchHousekeepingTasks,
  markHousekeepingTaskDone,
} from '../../api/housekeeping.js';
import { listRoomTypesFull } from '../../api/pricing.js';
import type { BookingDto, RoomDb } from '../../api/rooms.js';
import { isUnpaid, unpaidAmount } from '../../lib/paymentStatus.js';
import { useAuth } from '../../stores/auth.js';
import { useCheckoutModal } from '../../stores/checkout.js';
import { useWalkInWizard } from '../../stores/walkIn.js';
import { OverstayAlert } from '../OverstayAlert.js';
import { UnpaidBadge } from '../UnpaidBadge.js';
import { formatRelative, formatTime, mapRoomStatus } from '../dashboard-v2/lib.js';

interface Props {
  room: RoomDb | null;
  bookings: BookingDto[];
  now: Date;
  isOpen: boolean;
  onClose: () => void;
}

export function RoomDetailDrawer({ room, bookings, now, isOpen, onClose }: Props) {
  // ESC keypress chiude
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop scuro - button per a11y keyboard fallback */}
      <button
        type="button"
        aria-label="Chiudi pannello"
        onClick={onClose}
        data-testid="drawer-backdrop"
        className={cn(
          'fixed inset-0 z-40 bg-[rgba(45,36,29,0.5)] cursor-default transition-opacity duration-300',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
      />
      {/* Drawer panel */}
      <aside
        data-testid="room-detail-drawer"
        data-open={isOpen}
        className={cn(
          // HM-600 iPad UX: larghezza comoda su tablet (min 440 / 92vw).
          'fixed top-0 right-0 z-50 h-[var(--app-vh,100dvh)] w-[min(440px,92vw)] bg-bg-card shadow-hm-xl',
          'transition-transform duration-300 ease-out flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {room ? (
          <DrawerContent room={room} bookings={bookings} now={now} onClose={onClose} />
        ) : null}
      </aside>
    </>
  );
}

function DrawerContent({
  room,
  bookings,
  now,
  onClose,
}: {
  room: RoomDb;
  bookings: BookingDto[];
  now: Date;
  onClose: () => void;
}) {
  const status = mapRoomStatus(room.status);
  const userRole = useAuth((s) => s.user?.role);
  const openWalkIn = useWalkInWizard((s) => s.open);
  const openCheckout = useCheckoutModal((s) => s.open);
  const queryClient = useQueryClient();

  const activeBooking = bookings.find((b) => b.roomId === room.id && b.status === 'checked_in');

  // Fix camere: tipologia + listino base dalla query ['room-types'] gia'
  // esistente altrove (Impostazioni/Planning usano la stessa key/endpoint):
  // cache condivisa, nessun endpoint nuovo.
  const roomTypesQuery = useQuery({
    queryKey: ['room-types'],
    queryFn: listRoomTypesFull,
    staleTime: 60_000,
  });
  const roomType = roomTypesQuery.data?.roomTypes.find((t) => t.id === room.roomTypeId) ?? null;

  const canCheckin = room.status === 'free';
  const canCheckout = room.status === 'occupied' && !!activeBooking;
  const isHousekeeper = userRole === 'housekeeping' || userRole === 'admin';

  // ONDATA 2 P6c: 'Marca pulita' era senza onClick. Ora: lookup del task
  // housekeeping della camera → POST /tasks/:id/done (dirty→free lato
  // server). Fallback admin per camere dirty storiche senza task:
  // PATCH /rooms/:id status free. Invalida la query rooms per refresh.
  const [cleaning, setCleaning] = useState(false);
  const [cleanError, setCleanError] = useState<string | null>(null);
  const markClean = async () => {
    if (cleaning) return;
    setCleaning(true);
    setCleanError(null);
    try {
      const { tasks } = await fetchHousekeepingTasks();
      const task = tasks.find(
        (t) => t.roomId === room.id && (t.status === 'pending' || t.status === 'in_progress'),
      );
      if (task) {
        await markHousekeepingTaskDone(task.id);
      } else if (userRole === 'admin') {
        await adminSetRoomFree(room.id);
      } else {
        setCleanError('Nessun task pulizia per questa camera: contatta un admin.');
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['rooms'] }),
        queryClient.invalidateQueries({ queryKey: ['housekeeping-tasks'] }),
      ]);
    } catch (err) {
      setCleanError((err as Error).message || 'Errore imprevisto, riprova.');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <>
      <header className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-border-base">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-text-primary m-0">Camera {room.code}</h2>
          <span
            className={cn(
              'text-[10px] font-semibold uppercase tracking-[0.3px] px-2.5 py-[3px] rounded-[5px]',
              status.tileBgClass,
              status.tileStatusClass,
            )}
          >
            {status.abbr}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="drawer-close"
          aria-label="Chiudi"
          className="h-11 w-11 min-h-[44px] min-w-[44px] flex items-center justify-center text-text-muted hover:text-text-primary rounded-md hover:bg-bg-muted"
        >
          <X size={18} />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        <section className="space-y-0">
          <Row label="Stato" value={status.label} />
          {activeBooking ? (
            (() => {
              // HM-530 fix camere: replica logica RoomDetailPanel HM-452
              // per mostrare overstay + extras + currentTotalDue anche in
              // /camere (prima il drawer mostrava solo paidAmount/totalAmount).
              const endMs = new Date(activeBooking.endAt).getTime();
              const overstayMs = now.getTime() - endMs;
              const overstayDays = overstayMs > 0 ? Math.floor(overstayMs / 86_400_000) : 0;
              const baseTotal = Number(activeBooking.totalAmount);
              const currentDue = activeBooking.currentTotalDue
                ? Number(activeBooking.currentTotalDue)
                : baseTotal;
              const extraSum = currentDue - baseTotal;
              const paid = Number(activeBooking.paidAmount);
              // Fix chiavi fisiche: il codice accesso VERO non arriva nel
              // payload GET /bookings (negli hotel accessMethod='key' la
              // cascade riceve {skipped: true, reason: 'physical_keys'} da
              // /access-codes). Prima qui finiva il publicCode del booking
              // (HM-2026-...) spacciato per codice accesso: fuorviante.
              // Se il codice non esiste → "Chiave fisica", MAI publicCode.
              const accessCode =
                (activeBooking as BookingDto & { accessCode?: string | null }).accessCode ?? null;
              return (
                <>
                  <Row label="Ospite" value={activeBooking.guestName ?? 'Ospite'} />
                  <Row
                    label="Servizio"
                    value={activeBooking.service === 'day_use' ? 'Day Use' : 'Pernottamento'}
                  />
                  {roomType && <Row label="Tipologia" value={roomType.label} />}
                  {accessCode ? (
                    <Row label="Codice accesso" value={accessCode} mono />
                  ) : (
                    <div
                      className="flex justify-between py-2 text-[13px]"
                      data-testid="drawer-access-key"
                    >
                      <span className="text-text-secondary">Accesso</span>
                      <span className="font-medium text-text-primary flex items-center gap-1.5">
                        <KeyRound size={14} strokeWidth={HM_ICON_STROKE_WIDTH} />
                        Chiave fisica
                      </span>
                    </div>
                  )}
                  <Row
                    label="Scadenza"
                    value={`${formatTime(new Date(activeBooking.endAt))} (${formatRelative(new Date(activeBooking.endAt), now)})`}
                    warning
                  />
                  {/* ONDATA 2 P1c: allarme rosso forte (non piu' Row soft);
                      overstayDays > 3 ⇒ cap HM_OVERSTAY_MAX_CYCLES raggiunto
                      → 'CONTATORE FERMO'. */}
                  <OverstayAlert days={overstayDays} />
                  {extraSum > 0 && (
                    <Row label="Extras" value={`+€ ${extraSum.toFixed(2)}`} warning />
                  )}
                  <Row label="Base" value={`€ ${baseTotal.toFixed(2)}`} />
                  <Row label="Totale dovuto" value={`€ ${currentDue.toFixed(2)}`} mono />
                  <Row
                    label="Pagamento"
                    value={
                      paid >= currentDue
                        ? 'pagato'
                        : `€ ${paid.toFixed(2)} / € ${currentDue.toFixed(2)}`
                    }
                  />
                  {/* Revisione pagamenti 2026-07-22: Row dedicata badge rosso. */}
                  {isUnpaid(activeBooking) && (
                    <div className="flex justify-between items-center py-2 text-[13px]">
                      <span className="text-text-secondary">Da incassare</span>
                      <UnpaidBadge amount={unpaidAmount(activeBooking)} />
                    </div>
                  )}
                </>
              );
            })()
          ) : (
            <>
              <Row label="Piano" value={room.floor} />
              <Row label="Edificio" value={room.building} />
              {/* Fix camere: camera senza booking attivo — tipologia +
                  prezzi base dal listino della tipologia (solo se il
                  payload room-types e' disponibile). */}
              {roomType && (
                <>
                  <Row label="Tipologia" value={roomType.label} />
                  <Row label="Day use" value={`€ ${Number(roomType.basePriceDayUse).toFixed(2)}`} />
                  <Row
                    label="Pernottamento"
                    value={`€ ${Number(roomType.basePriceOvernight).toFixed(2)}`}
                  />
                </>
              )}
            </>
          )}
        </section>

        <section className="mt-5">
          <h3 className="text-xs uppercase font-semibold text-text-secondary tracking-wide mb-2.5">
            Azioni rapide
          </h3>
          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              data-testid="drawer-action-checkin"
              onClick={() => openWalkIn(room)}
              disabled={!canCheckin}
            >
              <CircleCheck size={18} strokeWidth={HM_ICON_STROKE_WIDTH} />
              Check-in walk-in
            </Button>
            <Button
              variant="primary"
              data-testid="drawer-action-checkout"
              onClick={() => activeBooking && openCheckout(room, activeBooking)}
              disabled={!canCheckout}
            >
              <BedDouble size={18} strokeWidth={HM_ICON_STROKE_WIDTH} />
              Check-out
            </Button>
            <RoleGuardedAction
              variant="danger"
              allowed={isHousekeeper}
              icon={TriangleAlert}
              label="Segnala guasto"
              testid="drawer-action-issue"
              hint="Disponibile per ruolo housekeeping"
            />
            <RoleGuardedAction
              variant="secondary"
              allowed={isHousekeeper && room.status === 'dirty' && !cleaning}
              icon={Wind}
              label={cleaning ? 'Marco pulita…' : 'Marca pulita'}
              testid="drawer-action-clean"
              onClick={markClean}
              hint={
                !isHousekeeper
                  ? 'Disponibile per ruolo housekeeping'
                  : 'Solo per camere in stato Da pulire'
              }
            />
            {cleanError && (
              <p
                data-testid="drawer-clean-error"
                className="text-[12px] text-[#A8453A] font-medium m-0"
              >
                {cleanError}
              </p>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button variant="secondary" disabled data-testid="drawer-action-whatsapp">
                    <MessageCircle size={18} strokeWidth={HM_ICON_STROKE_WIDTH} />
                    WhatsApp ospite
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Driver WhatsApp arriva in cluster 12 onboarding</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button variant="ghost" disabled data-testid="drawer-action-config">
                    <Wrench size={18} strokeWidth={HM_ICON_STROKE_WIDTH} />
                    Configura camera
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Cluster 16 Impostazioni</TooltipContent>
            </Tooltip>
          </div>
        </section>
      </div>
    </>
  );
}

function Row({
  label,
  value,
  mono,
  warning,
}: {
  label: string;
  value: string;
  mono?: boolean;
  warning?: boolean;
}) {
  return (
    <div className="flex justify-between py-2 text-[13px]">
      <span className="text-text-secondary">{label}</span>
      <span
        className={cn(
          'font-medium',
          mono && 'font-mono tracking-[2px] text-accent-primary-text font-semibold',
          warning && 'text-accent-primary-text font-semibold',
          !mono && !warning && 'text-text-primary',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function RoleGuardedAction({
  variant,
  allowed,
  icon: Icon,
  label,
  testid,
  hint,
  onClick,
}: {
  variant: 'danger' | 'secondary';
  allowed: boolean;
  icon: LucideIcon;
  label: string;
  testid: string;
  hint: string;
  /** ONDATA 2 P6c: azione collegata (prima i bottoni erano inerti). */
  onClick?: () => void;
}) {
  const button = (
    <Button variant={variant} disabled={!allowed} data-testid={testid} onClick={onClick}>
      <Icon size={18} strokeWidth={HM_ICON_STROKE_WIDTH} />
      {label}
    </Button>
  );
  if (allowed) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{button}</span>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}
